import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { FileSearch, RefreshCw, ShieldAlert, AlertTriangle, Bed, FileSpreadsheet, FileText } from 'lucide-react';

export default function SleepoverEstimateComparisonPage() {
  const [flagOn, setFlagOn] = useState(null);
  const [days, setDays] = useState('30');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (chosenDays = days) => {
    setLoading(true);
    try {
      const flags = await api.get('/production/feature-flags/mine').catch(() => ({ data: {} }));
      const enabled = !!(flags.data?.data || flags.data || {}).sleepover_billing_enabled;
      setFlagOn(enabled);
      if (!enabled) { setData(null); return; }
      const r = await api.get(`/admin/sleepover/estimate-comparison?days=${chosenDays}`);
      setData(r.data?.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load comparison');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleExport = async (fmt) => {
    try {
      const res = await api.get(`/admin/sleepover/estimate-comparison/export?days=${days}&fmt=${fmt}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: fmt === 'csv' ? 'text/csv' : 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sleepover_estimate_${data?.range?.from || 'report'}_to_${data?.range?.to || ''}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch (e) {
      toast.error(`Failed to export ${fmt.toUpperCase()}`);
    }
  };

  return (
    <div className="p-6 max-w-6xl space-y-4" data-testid="sleepover-comparison-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <FileSearch className="w-5 h-5" /> Sleep-Over Estimate Comparison
        </h1>
        <p className="text-sm text-muted-foreground">Read-only finance validation report. No invoices are generated.</p>
      </div>

      {flagOn === false && (
        <Alert className="border-amber-300 bg-amber-50">
          <ShieldAlert className="w-4 h-4" />
          <AlertTitle>Feature flag disabled</AlertTitle>
          <AlertDescription>Enable <b>sleepover_billing_enabled</b> first.</AlertDescription>
        </Alert>
      )}

      {flagOn && (
        <>
          <div className="flex items-center gap-2">
            <Select value={days} onValueChange={(v) => { setDays(v); load(v); }}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="comparison-days"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={() => load()} data-testid="comparison-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
            <Button size="sm" variant="outline" onClick={() => handleExport('csv')} disabled={!data || !data.rows?.length} data-testid="export-csv"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> CSV</Button>
            <Button size="sm" variant="outline" onClick={() => handleExport('pdf')} disabled={!data || !data.rows?.length} data-testid="export-pdf"><FileText className="w-3.5 h-3.5 mr-1" /> PDF</Button>
            <Badge className="bg-amber-200 text-amber-900 border-amber-400 ml-auto">PREVIEW · Estimate only — not yet invoiced</Badge>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8"><RefreshCw className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : !data ? (
            <p className="text-sm text-muted-foreground">No data.</p>
          ) : (
            <>
              <div className="grid grid-cols-5 gap-2">
                <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold" data-testid="kpi-shifts">{data.totals.shifts}</p><p className="text-[10px] text-muted-foreground uppercase">Shifts</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold">{data.totals.active_intervals}</p><p className="text-[10px] text-muted-foreground uppercase">Active intervals</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold font-mono">${data.totals.flat_total.toFixed(2)}</p><p className="text-[10px] text-muted-foreground uppercase">Flat total</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold font-mono">${data.totals.active_total.toFixed(2)}</p><p className="text-[10px] text-muted-foreground uppercase">Active total</p></CardContent></Card>
                <Card className="border-amber-400 bg-amber-50"><CardContent className="p-3 text-center"><p className="text-2xl font-bold font-mono text-amber-900" data-testid="kpi-grand-total">${data.totals.grand_total_estimate.toFixed(2)}</p><p className="text-[10px] text-amber-900 uppercase">Grand total est.</p></CardContent></Card>
              </div>

              <Card>
                <CardHeader className="pb-2 px-3 pt-3"><CardTitle className="text-sm flex items-center gap-2"><Bed className="w-4 h-4" /> Shifts ({data.totals.shifts}) · {data.range.from} → {data.range.to}</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Date</TableHead>
                        <TableHead className="text-xs">Time</TableHead>
                        <TableHead className="text-xs">Participant</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                        <TableHead className="text-xs">Intervals</TableHead>
                        <TableHead className="text-xs text-right">Raw h</TableHead>
                        <TableHead className="text-xs text-right">Billed h</TableHead>
                        <TableHead className="text-xs text-right">Flat</TableHead>
                        <TableHead className="text-xs text-right">Active</TableHead>
                        <TableHead className="text-xs text-right">Total est.</TableHead>
                        <TableHead className="text-xs">Flags</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.rows.length === 0 && (
                        <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground text-sm py-4">No SLEEP_OVER shifts in range.</TableCell></TableRow>
                      )}
                      {data.rows.map((r) => (
                        <TableRow key={r.shift_id} data-testid={`comparison-row-${r.shift_id}`}>
                          <TableCell className="text-xs font-mono">{r.date}</TableCell>
                          <TableCell className="text-xs font-mono">{r.start_time}–{r.end_time}</TableCell>
                          <TableCell className="text-xs">{r.participant_name || '—'}</TableCell>
                          <TableCell><Badge variant="outline" className="text-[9px]">{r.status}</Badge></TableCell>
                          <TableCell className="text-xs text-center">{r.n_active_intervals}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{r.raw_active_hours.toFixed(2)}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{r.billed_active_hours.toFixed(2)}</TableCell>
                          <TableCell className="text-xs text-right font-mono">${r.flat_amount.toFixed(2)}</TableCell>
                          <TableCell className="text-xs text-right font-mono">${r.active_amount.toFixed(2)}</TableCell>
                          <TableCell className="text-xs text-right font-mono font-semibold">${r.total_estimate.toFixed(2)}</TableCell>
                          <TableCell>
                            {!r.has_flat_service && <Badge variant="outline" className="text-[8px] mr-1">No flat svc</Badge>}
                            {r.warnings?.length > 0 && (
                              <Badge variant="outline" className="text-[8px] text-amber-700 border-amber-400">
                                <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> {r.warnings.length}
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Alert className="border-amber-300 bg-amber-50">
                <AlertDescription className="text-[11px] text-amber-900">
                  These totals are <b>estimates only</b>. Invoices, claims, and payroll have not been generated and are not affected by this report.
                  Once Phase 2 wiring is approved, the "Currently invoiced" and "Δ" columns will populate automatically for each shift.
                </AlertDescription>
              </Alert>
            </>
          )}
        </>
      )}
    </div>
  );
}
